import { useState, useMemo } from "react";
import { PropsView } from "./Models";
import Spinner from "@/atomic/atoms/Spinner";
import Modal from "@/atomic/atoms/Modal";
import LoggedMenu from "@/atomic/atoms/LoggedMenu";
import OrgPayment from "@/atomic/organisms/orgPayment";
import DirectPaymentFooter from "@/atomic/organisms/DirectPaymentFooter";
import LanguageSelector from "@/atomic/atoms/LanguageSelector";
import Icon from "@/atomic/atoms/Icon";

const formatPlate = (plate: string) =>
  plate.replace(/^([A-Za-z]{3})(\d[A-Za-z]?\d{2,3})$/, "$1-$2");

const getItemData = (item: any) => {
  return {
    plate: item.plate ? formatPlate(item.plate) : "",
    date: item.date,
    status: item.nmPraca || item.status || "Passagem paga",
    price: `R$ ${item.price}`,
    nmConcessionaria: item.nmConcessionaria,
  };
};

type FilterOrder = "todas" | "pendentes" | "pagos";
type FilterPeriod = "30" | "60" | "90";

const PERIOD_OPTIONS: { value: FilterPeriod; label: string }[] = [
  { value: "30", label: "30 dias" },
  { value: "60", label: "60 dias" },
  { value: "90", label: "90 dias" },
];

const ORDER_OPTIONS: { value: FilterOrder; label: string }[] = [
  { value: "todas", label: "Todas" },
  { value: "pendentes", label: "Em aberto" },
  { value: "pagos", label: "Pagas" },
];

export default function HomeView({
  goToVehicles,
  items,
  isLoading,
  isShowingMenu,
  toggleMenu,
  plate,
  isVisibleRecharge,
  setIsVisibleRecharge,
  goToRecharge,
  hasVehicles,
  goToBuy,
  debts,
  cart,
  onChangeDebts,
  hasPendingAttemptPayment,
  handleDisablePendingAttemptPayment,
  activeTab,
  onTabChange,
  goToNewCard,
  isAutoPaymentEnabled = true,
  goToNotifications,
  isNotificationsEnabled = false,
  vehicles,
}: PropsView) {
  const primaryPlate = Array.isArray(plate) ? plate[0] || "" : plate || "";
  const formattedPrimaryPlate = primaryPlate ? formatPlate(primaryPlate) : "";
  const selectedVehicle = vehicles?.find(v => v.plate?.toUpperCase() === primaryPlate.toUpperCase());
  const vehicleLabel = [selectedVehicle?.brand, selectedVehicle?.model].filter(Boolean).join(" ");

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<FilterPeriod>("30");
  const [selectedOrder, setSelectedOrder] = useState<FilterOrder>("todas");

  const handleApplyFilter = () => {
    onTabChange(selectedOrder);
    setIsFilterOpen(false);
  };

  const handleClearFilter = () => {
    setSelectedPeriod("30");
    setSelectedOrder("todas");
    onTabChange("todas");
    setIsFilterOpen(false);
  };

  const showPending = activeTab === "pendentes" || activeTab === "todas";
  const showPaid = activeTab === "pagos" || activeTab === "todas";
  const showAll = activeTab === "todas";
  const hasMultiplePlates = Array.isArray(plate) && plate.length > 1;

  const groupedDebts = useMemo(() => {
    if (!debts || !hasMultiplePlates) return null;
    const groups: Record<string, typeof debts> = {};
    for (const item of debts) {
      const key = item.plate?.toUpperCase() || "";
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    }
    return groups;
  }, [debts, hasMultiplePlates]);

  const groupedPaidItems = useMemo(() => {
    if (!items || !hasMultiplePlates) return null;
    const groups: Record<string, any[]> = {};
    for (const item of items) {
      const data = getItemData(item);
      const key = (item as any).plate?.toUpperCase() || data.plate?.replace("-", "").toUpperCase() || "";
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    }
    return groups;
  }, [items, hasMultiplePlates]);

  const allPlateKeys = useMemo(() => {
    if (!hasMultiplePlates || !showAll) return null;
    const keys = new Set<string>();
    if (groupedDebts) Object.keys(groupedDebts).forEach(k => keys.add(k));
    if (groupedPaidItems) Object.keys(groupedPaidItems).forEach(k => keys.add(k));
    return Array.from(keys);
  }, [hasMultiplePlates, showAll, groupedDebts, groupedPaidItems]);

  return (
    <div className="relative flex flex-1 flex-col w-full min-h-screen bg-background">
      {/* Header */}
      <header className="w-full bg-tertiary">
        <div className="relative flex items-center h-[75px] border-b border-white/10">
          <button
            onClick={toggleMenu}
            type="button"
            className="bg-black flex items-center justify-center w-[75px] h-[75px] shrink-0"
            aria-label={isShowingMenu ? "Fechar menu" : "Menu"}
          >
            {isShowingMenu ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            ) : (
              <img
                src="/svg/icon-menu-white-2.0.svg"
                alt="menu"
                className="w-[22px] h-[17px]"
              />
            )}
          </button>
          <Icon className="dark ml-4" width="96" height="30" />
          <div className="ml-auto mr-3">
            <LanguageSelector
              className="text-white"
              showFlag={true}
            />
          </div>
        </div>
      </header>

      {/* Seção escura — veículo */}
      <div className="bg-tertiary">
        <div className="w-full">
          <div className="w-full max-w-[480px] mx-auto px-5 pt-4 pb-6">
            <p className="text-white text-sm font-secondary tracking-[-0.4px] leading-[1.12]">
              Seus carros:
            </p>
            <div className="mt-3">
              <button
                type="button"
                onClick={() => (hasVehicles ? goToVehicles(plate) : goToBuy())}
                className="w-full bg-white rounded-[16px] px-4 py-5 flex items-center gap-4 min-h-[88px] text-left"
              >
                <img src="/svg/car-model.svg" className="w-[72px] h-[44px] shrink-0" alt="" />
                <div className="flex-1 flex flex-col gap-0.5 text-text tracking-[-0.4px]">
                  {Array.isArray(plate) && plate.length > 1 ? (
                    <>
                      <p className="text-[20px] font-bold font-secondary leading-[1.2]">
                        {String(plate.length).padStart(2, "0")} veículos
                      </p>
                      <p className="text-sm font-secondary">
                        selecionados
                      </p>
                    </>
                  ) : (
                    <>
                      {vehicleLabel && (
                        <p className="text-sm font-secondary">
                          {vehicleLabel}
                        </p>
                      )}
                      <p className="text-[20px] font-bold font-secondary leading-[1.2] uppercase">
                        {formattedPrimaryPlate || "Nenhuma placa"}
                      </p>
                    </>
                  )}
                </div>
                <img src="/svg/icon-arrow-right-2.0.svg" className="w-[9px] h-4 shrink-0" alt="" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Seção principal — passagens */}
      <main className="flex-1 bg-background pb-36">
        <div className="w-full">
          <div className="w-full max-w-[480px] mx-auto px-4">
            <div className="pt-5 flex items-center justify-between">
              <h2 className="text-base font-bold font-primary text-text tracking-[-0.1px]">
                Passagens
              </h2>
              <button
                type="button"
                onClick={() => setIsFilterOpen(true)}
                className="flex items-center gap-2"
              >
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white">
                  <svg width="16" height="14" viewBox="0 0 19 16" fill="none" className="shrink-0">
                    <path
                      d="M1.5 1h16l-6.4 7.56v5.24L7.9 15.2V8.56L1.5 1Z"
                      stroke="#303030"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span className="text-[11px] uppercase tracking-[0.77px] text-[#303030] font-secondary">
                  Filtrar
                </span>
              </button>
            </div>
            <div className="w-full h-px bg-border mt-4" />

            {/* Selecionar todos — visível quando há débitos pendentes */}
            {showPending && !isLoading && debts && debts.length > 0 && (
              <div className="flex items-center gap-4 py-4">
                <span
                  role="checkbox"
                  aria-checked={debts?.length === cart?.length}
                  tabIndex={0}
                  onClick={() => {
                    const syntheticEvent = {
                      target: {
                        name: "all",
                        checked: debts?.length !== cart?.length,
                      },
                    } as React.ChangeEvent<HTMLInputElement>;
                    onChangeDebts?.(syntheticEvent);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      const syntheticEvent = {
                        target: {
                          name: "all",
                          checked: debts?.length !== cart?.length,
                        },
                      } as React.ChangeEvent<HTMLInputElement>;
                      onChangeDebts?.(syntheticEvent);
                    }
                  }}
                  className={`w-6 h-6 rounded-full border-2 shrink-0 cursor-pointer transition-colors flex items-center justify-center ${
                    debts?.length === cart?.length
                      ? "bg-success border-success shadow-[inset_0_0_0_2px_white]"
                      : "border-success bg-transparent"
                  }`}
                />
                <p className="text-base font-bold font-primary text-tertiary tracking-[-0.1px]">
                  Selecionar todos
                </p>
              </div>
            )}

            {/* Agrupamento unificado — "todas" com múltiplas placas */}
            {showAll && hasMultiplePlates && allPlateKeys && !isLoading && (
              <div className="flex flex-col gap-2">
                {allPlateKeys.map((plateKey) => {
                  const plateDebts = groupedDebts?.[plateKey] || [];
                  const platePaid = groupedPaidItems?.[plateKey] || [];
                  if (plateDebts.length === 0 && platePaid.length === 0) return null;
                  return (
                    <div key={plateKey}>
                      <p className="text-base font-bold font-primary text-text tracking-[-0.1px] pt-4 pb-2 uppercase">
                        {formatPlate(plateKey)}
                      </p>
                      <div className="flex flex-col gap-4">
                        {plateDebts.map((item, index) => {
                          const isSelected =
                            cart?.some(
                              (selectable) =>
                                selectable.name === item.id && selectable.checked
                            ) ?? false;
                          return (
                            <label
                              key={item.id || index}
                              className={`bg-white rounded-[16px] p-5 flex cursor-pointer transition-all ${
                                isSelected
                                  ? "border border-[#e5729b]"
                                  : "border border-transparent"
                              }`}
                            >
                              <input
                                type="checkbox"
                                name={item.id}
                                className="sr-only"
                                onChange={onChangeDebts}
                                checked={isSelected}
                              />
                              <div className="flex items-center w-full">
                                <div className="flex-1 flex flex-col gap-1">
                                  <span
                                    className={`inline-flex self-start px-1 rounded text-xs font-medium font-secondary tracking-[-0.4px] leading-[16.8px] ${
                                      isSelected
                                        ? "bg-primary text-white"
                                        : "bg-[#e6e6e6] text-[#696966]"
                                    }`}
                                  >
                                    {formatPlate(item.plate)}
                                  </span>
                                  <p className="text-xs text-text font-secondary tracking-[-0.4px] leading-[16.8px]">
                                    {item.original?.nmConcessionaria || item.nmConcessionaria}
                                  </p>
                                </div>
                                <div className="flex flex-col gap-1 items-end text-right">
                                  {item.hasDate && (
                                    <p className="text-xs text-[#696966] font-secondary tracking-[-0.4px] leading-[16.8px]">
                                      {item.daysLeft > 0
                                        ? `Vencido há ${item.daysLeft} dia(s)`
                                        : `Vencimento ${item.dtVencimento?.substring(0, 5)}`}
                                    </p>
                                  )}
                                  <p className="text-lg font-medium text-text font-secondary tracking-[-0.4px] leading-[21.6px]">
                                    R$ {item.price}
                                  </p>
                                </div>
                              </div>
                            </label>
                          );
                        })}
                        {platePaid.map((item, index) => {
                          const itemData = getItemData(item);
                          return (
                            <div
                              key={`paid-${plateKey}-${index}`}
                              className="bg-white rounded-[16px] p-5 flex opacity-40 border border-transparent"
                            >
                              <div className="flex items-center w-full">
                                <div className="flex-1 flex flex-col gap-1">
                                  <span className="inline-flex self-start px-1 rounded text-xs font-medium font-secondary tracking-[-0.4px] leading-[16.8px] bg-[#e6e6e6] text-[#696966]">
                                    {itemData.plate}
                                  </span>
                                  <p className="text-xs text-text font-secondary tracking-[-0.4px] leading-[16.8px]">
                                    {itemData.nmConcessionaria}
                                  </p>
                                </div>
                                <div className="flex flex-col gap-1 items-end text-right">
                                  <p className="text-xs text-[#696966] font-secondary tracking-[-0.4px] leading-[16.8px]">
                                    Paga
                                  </p>
                                  <p className="text-lg font-medium text-text font-secondary tracking-[-0.4px] leading-[21.6px]">
                                    {itemData.price}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pendentes — sem agrupamento unificado */}
            {showPending && !showAll && (
              <>
                {!isLoading && debts && debts.length > 0 && (
                  hasMultiplePlates && groupedDebts ? (
                    <div className="flex flex-col gap-2">
                      {Object.entries(groupedDebts).map(([plateKey, groupItems]) => (
                        <div key={plateKey}>
                          <p className="text-base font-bold font-primary text-text tracking-[-0.1px] pt-4 pb-2 uppercase">
                            {formatPlate(plateKey)}
                          </p>
                          <div className="flex flex-col gap-4">
                            {groupItems.map((item, index) => {
                              const isSelected =
                                cart?.some(
                                  (selectable) =>
                                    selectable.name === item.id && selectable.checked
                                ) ?? false;
                              return (
                                <label
                                  key={item.id || index}
                                  className={`bg-white rounded-[16px] p-5 flex cursor-pointer transition-all ${
                                    isSelected
                                      ? "border border-[#e5729b]"
                                      : "border border-transparent"
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    name={item.id}
                                    className="sr-only"
                                    onChange={onChangeDebts}
                                    checked={isSelected}
                                  />
                                  <div className="flex items-center w-full">
                                    <div className="flex-1 flex flex-col gap-1">
                                      <span
                                        className={`inline-flex self-start px-1 rounded text-xs font-medium font-secondary tracking-[-0.4px] leading-[16.8px] ${
                                          isSelected
                                            ? "bg-primary text-white"
                                            : "bg-[#e6e6e6] text-[#696966]"
                                        }`}
                                      >
                                        {formatPlate(item.plate)}
                                      </span>
                                      <p className="text-xs text-text font-secondary tracking-[-0.4px] leading-[16.8px]">
                                        {item.original?.nmConcessionaria || item.nmConcessionaria}
                                      </p>
                                    </div>
                                    <div className="flex flex-col gap-1 items-end text-right">
                                      {item.hasDate && (
                                        <p className="text-xs text-[#696966] font-secondary tracking-[-0.4px] leading-[16.8px]">
                                          {item.daysLeft > 0
                                            ? `Vencido há ${item.daysLeft} dia(s)`
                                            : `Vencimento ${item.dtVencimento?.substring(0, 5)}`}
                                        </p>
                                      )}
                                      <p className="text-lg font-medium text-text font-secondary tracking-[-0.4px] leading-[21.6px]">
                                        R$ {item.price}
                                      </p>
                                    </div>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-4">
                      {debts.map((item, index) => {
                        const isSelected =
                          cart?.some(
                            (selectable) =>
                              selectable.name === item.id && selectable.checked
                          ) ?? false;
                        return (
                          <label
                            key={item.id || index}
                            className={`bg-white rounded-[16px] p-5 flex cursor-pointer transition-all ${
                              isSelected
                                ? "border border-[#e5729b]"
                                : "border border-transparent"
                            }`}
                          >
                            <input
                              type="checkbox"
                              name={item.id}
                              className="sr-only"
                              onChange={onChangeDebts}
                              checked={isSelected}
                            />
                            <div className="flex items-center w-full">
                              <div className="flex-1 flex flex-col gap-1">
                                <span
                                  className={`inline-flex self-start px-1 rounded text-xs font-medium font-secondary tracking-[-0.4px] leading-[16.8px] ${
                                    isSelected
                                      ? "bg-primary text-white"
                                      : "bg-[#e6e6e6] text-[#696966]"
                                  }`}
                                >
                                  {formatPlate(item.plate)}
                                </span>
                                <p className="text-xs text-text font-secondary tracking-[-0.4px] leading-[16.8px]">
                                  {item.original?.nmConcessionaria || item.nmConcessionaria}
                                </p>
                              </div>
                              <div className="flex flex-col gap-1 items-end text-right">
                                {item.hasDate && (
                                  <p className="text-xs text-[#696966] font-secondary tracking-[-0.4px] leading-[16.8px]">
                                    {item.daysLeft > 0
                                      ? `Vencido há ${item.daysLeft} dia(s)`
                                      : `Vencimento ${item.dtVencimento?.substring(0, 5)}`}
                                  </p>
                                )}
                                <p className="text-lg font-medium text-text font-secondary tracking-[-0.4px] leading-[21.6px]">
                                  R$ {item.price}
                                </p>
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )
                )}

                {!isLoading && (!debts || debts.length === 0) && (
                  <div className="w-full flex justify-center items-center flex-col mt-10">
                    <img
                      src="/svg/icon-vehicle-not-found.svg"
                      width={48}
                      height={48}
                      alt=""
                      className="mt-6"
                    />
                    <h2 className="mt-4 text-sm text-text text-center px-8 leading-relaxed">
                      Nenhuma passagem em aberto encontrada. As passagens podem
                      levar até 48 horas para ficarem disponíveis no sistema para
                      pagamento.
                    </h2>
                  </div>
                )}
              </>
            )}

            {/* Pagos — sem agrupamento unificado */}
            {showPaid && !showAll && (
              <>
                {!isLoading && items && items.length > 0 && (
                  hasMultiplePlates && groupedPaidItems ? (
                    <div className="flex flex-col gap-2 mt-4">
                      {Object.entries(groupedPaidItems).map(([plateKey, paidGroup]) => (
                        <div key={`paid-group-${plateKey}`}>
                          <p className="text-base font-bold font-primary text-text tracking-[-0.1px] pt-4 pb-2 uppercase">
                            {formatPlate(plateKey)}
                          </p>
                          <div className="flex flex-col gap-4">
                            {paidGroup.map((item, index) => {
                              const itemData = getItemData(item);
                              return (
                                <div
                                  key={`paid-${plateKey}-${index}`}
                                  className="bg-white rounded-[16px] p-5 flex opacity-40 border border-transparent"
                                >
                                  <div className="flex items-center w-full">
                                    <div className="flex-1 flex flex-col gap-1">
                                      <span className="inline-flex self-start px-1 rounded text-xs font-medium font-secondary tracking-[-0.4px] leading-[16.8px] bg-[#e6e6e6] text-[#696966]">
                                        {itemData.plate}
                                      </span>
                                      <p className="text-xs text-text font-secondary tracking-[-0.4px] leading-[16.8px]">
                                        {itemData.nmConcessionaria}
                                      </p>
                                    </div>
                                    <div className="flex flex-col gap-1 items-end text-right">
                                      <p className="text-xs text-[#696966] font-secondary tracking-[-0.4px] leading-[16.8px]">
                                        Paga
                                      </p>
                                      <p className="text-lg font-medium text-text font-secondary tracking-[-0.4px] leading-[21.6px]">
                                        {itemData.price}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-4 mt-4">
                      {items.map((item, index) => {
                        const itemData = getItemData(item);
                        return (
                          <div
                            key={`paid-${index}`}
                            className="bg-white rounded-[16px] p-5 flex opacity-40 border border-transparent"
                          >
                            <div className="flex items-center w-full">
                              <div className="flex-1 flex flex-col gap-1">
                                <span className="inline-flex self-start px-1 rounded text-xs font-medium font-secondary tracking-[-0.4px] leading-[16.8px] bg-[#e6e6e6] text-[#696966]">
                                  {itemData.plate}
                                </span>
                                <p className="text-xs text-text font-secondary tracking-[-0.4px] leading-[16.8px]">
                                  {itemData.nmConcessionaria}
                                </p>
                              </div>
                              <div className="flex flex-col gap-1 items-end text-right">
                                <p className="text-xs text-[#696966] font-secondary tracking-[-0.4px] leading-[16.8px]">
                                  Paga
                                </p>
                                <p className="text-lg font-medium text-text font-secondary tracking-[-0.4px] leading-[21.6px]">
                                  {itemData.price}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )
                )}

                {!isLoading && (!items || items.length === 0) && (
                  <div className="w-full flex justify-center items-center flex-col mt-10">
                    <img
                      src="/svg/icon-vehicle-not-found.svg"
                      width={48}
                      height={48}
                      alt=""
                      className="mt-6"
                    />
                    <h2 className="mt-4 text-sm text-text text-center">
                      Nenhuma passagem paga encontrada.
                    </h2>
                  </div>
                )}
              </>
            )}

            {/* "Todas" com placa única ou flat — pendentes + pagas juntas */}
            {showAll && !hasMultiplePlates && !isLoading && (
              <>
                {debts && debts.length > 0 && (
                  <div className="flex flex-col gap-4">
                    {debts.map((item, index) => {
                      const isSelected =
                        cart?.some(
                          (selectable) =>
                            selectable.name === item.id && selectable.checked
                        ) ?? false;
                      return (
                        <label
                          key={item.id || index}
                          className={`bg-white rounded-[16px] p-5 flex cursor-pointer transition-all ${
                            isSelected
                              ? "border border-[#e5729b]"
                              : "border border-transparent"
                          }`}
                        >
                          <input
                            type="checkbox"
                            name={item.id}
                            className="sr-only"
                            onChange={onChangeDebts}
                            checked={isSelected}
                          />
                          <div className="flex items-center w-full">
                            <div className="flex-1 flex flex-col gap-1">
                              <span
                                className={`inline-flex self-start px-1 rounded text-xs font-medium font-secondary tracking-[-0.4px] leading-[16.8px] ${
                                  isSelected
                                    ? "bg-primary text-white"
                                    : "bg-[#e6e6e6] text-[#696966]"
                                }`}
                              >
                                {formatPlate(item.plate)}
                              </span>
                              <p className="text-xs text-text font-secondary tracking-[-0.4px] leading-[16.8px]">
                                {item.original?.nmConcessionaria || item.nmConcessionaria}
                              </p>
                            </div>
                            <div className="flex flex-col gap-1 items-end text-right">
                              {item.hasDate && (
                                <p className="text-xs text-[#696966] font-secondary tracking-[-0.4px] leading-[16.8px]">
                                  {item.daysLeft > 0
                                    ? `Vencido há ${item.daysLeft} dia(s)`
                                    : `Vencimento ${item.dtVencimento?.substring(0, 5)}`}
                                </p>
                              )}
                              <p className="text-lg font-medium text-text font-secondary tracking-[-0.4px] leading-[21.6px]">
                                R$ {item.price}
                              </p>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
                {items && items.length > 0 && (
                  <div className="flex flex-col gap-4 mt-4">
                    {items.map((item, index) => {
                      const itemData = getItemData(item);
                      return (
                        <div
                          key={`paid-${index}`}
                          className="bg-white rounded-[16px] p-5 flex opacity-40 border border-transparent"
                        >
                          <div className="flex items-center w-full">
                            <div className="flex-1 flex flex-col gap-1">
                              <span className="inline-flex self-start px-1 rounded text-xs font-medium font-secondary tracking-[-0.4px] leading-[16.8px] bg-[#e6e6e6] text-[#696966]">
                                {itemData.plate}
                              </span>
                              <p className="text-xs text-text font-secondary tracking-[-0.4px] leading-[16.8px]">
                                {itemData.nmConcessionaria}
                              </p>
                            </div>
                            <div className="flex flex-col gap-1 items-end text-right">
                              <p className="text-xs text-[#696966] font-secondary tracking-[-0.4px] leading-[16.8px]">
                                Paga
                              </p>
                              <p className="text-lg font-medium text-text font-secondary tracking-[-0.4px] leading-[21.6px]">
                                {itemData.price}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {(!debts || debts.length === 0) && (!items || items.length === 0) && (
                  <div className="w-full flex justify-center items-center flex-col mt-10">
                    <img
                      src="/svg/icon-vehicle-not-found.svg"
                      width={48}
                      height={48}
                      alt=""
                      className="mt-6"
                    />
                    <h2 className="mt-4 text-sm text-text text-center px-8 leading-relaxed">
                      Nenhuma passagem encontrada.
                    </h2>
                  </div>
                )}
              </>
            )}

            {/* "Todas" agrupado — empty state */}
            {showAll && hasMultiplePlates && !isLoading && (!debts || debts.length === 0) && (!items || items.length === 0) && (
              <div className="w-full flex justify-center items-center flex-col mt-10">
                <img
                  src="/svg/icon-vehicle-not-found.svg"
                  width={48}
                  height={48}
                  alt=""
                  className="mt-6"
                />
                <h2 className="mt-4 text-sm text-text text-center px-8 leading-relaxed">
                  Nenhuma passagem encontrada.
                </h2>
              </div>
            )}

            {isLoading && <Spinner />}
          </div>
        </div>
      </main>

      {!isLoading && (activeTab === "pendentes" || activeTab === "todas") && debts && debts.length > 0 && (
        <OrgPayment onClick={goToRecharge} />
      )}
      {!isLoading && (activeTab === "pendentes" || activeTab === "todas") && Array.isArray(debts) && debts.length === 0 && (
        <DirectPaymentFooter />
      )}
      <LoggedMenu isShowingMenu={isShowingMenu} toggleMenu={toggleMenu} />

      {/* Bottom sheet — Filtros */}
      {isFilterOpen && (
        <div className="fixed inset-0 z-20 w-full bg-black/30 flex items-end justify-center">
          <div className="w-full max-w-[480px] animate-slide-up">
            <div className="bg-white rounded-t-2xl px-6 pb-6 pt-2 flex flex-col">
              {/* Drag handle */}
              <div className="flex flex-col items-center py-2">
                <div className="w-11 h-1 bg-[#bfbfbf] rounded-[10px]" />
              </div>

              {/* Close button */}
              <div className="flex justify-end -mt-2 mb-1">
                <button
                  type="button"
                  onClick={() => setIsFilterOpen(false)}
                  className="p-2"
                  aria-label="Fechar filtros"
                >
                  <img src="/svg/icon-close.svg" alt="" className="w-5 h-5" />
                </button>
              </div>

              {/* Conteúdo */}
              <div className="flex flex-col gap-6">
                <h3 className="text-[28px] font-bold font-myFont text-[#4f4f4f] leading-[1.1] tracking-[-0.5px]">
                  Filtros
                </h3>

                {/* Ordenar por */}
                <div className="flex flex-col gap-[10px]">
                  <p className="text-[20px] font-bold font-myFont text-[#4f4f4f] tracking-[-0.1px] leading-[1.2]">
                    Ordenar por:
                  </p>
                  <div className="flex flex-col">
                    {ORDER_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setSelectedOrder(opt.value)}
                        className="flex items-center justify-between py-4"
                      >
                        <span className="text-base font-normal font-myFont text-[#6d7278] leading-[1.35]">
                          {opt.label}
                        </span>
                        <span
                          className={`w-6 h-6 rounded-full border-2 shrink-0 flex items-center justify-center transition-colors ${
                            selectedOrder === opt.value
                              ? "border-primary bg-primary"
                              : "border-[#bfbfbf] bg-transparent"
                          }`}
                        >
                          {selectedOrder === opt.value && (
                            <span className="w-3 h-3 rounded-full bg-white" />
                          )}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Atualizar */}
                <button
                  type="button"
                  onClick={handleApplyFilter}
                  className="w-full h-[60px] rounded-xl bg-primary text-white font-bold text-base font-myFont tracking-[-0.1px]"
                >
                  Atualizar
                </button>

                {/* Limpar filtros */}
                <button
                  type="button"
                  onClick={handleClearFilter}
                  className="w-full h-[60px] text-tertiary font-bold text-base font-myFont tracking-[-0.1px] underline"
                >
                  Limpar filtros
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isVisibleRecharge && (
        <Modal
          title="Tudo pronto!"
          messageElement={
            <p className="text-sm text-center text-gray-600">
              Quer cadastrar seu{" "}
              <span className="font-bold">
                Cartão
                <br />
                de Crédito
              </span>{" "}
              e facilitar seus pagamentos.
            </p>
          }
          actionMessage="Cadastrar"
          action={goToNewCard}
          actionSecondMessage="Agora não"
          actionSecond={() => setIsVisibleRecharge(false)}
        />
      )}

      {hasPendingAttemptPayment && isAutoPaymentEnabled && (
        <Modal
          title="Atenção!"
          messageElement={
            <p className="text-sm text-center text-gray-600">
              Não conseguimos processar o pagamento no
              <br />
              cartão cadastrado. Verifique os dados do cartão
              <br />
              e pague manualmente as passagens pendentes.
            </p>
          }
          actionMessage="Voltar"
          action={() => handleDisablePendingAttemptPayment()}
          actionClose={() => handleDisablePendingAttemptPayment()}
        />
      )}
    </div>
  );
}
